import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useRole, ROLES, PERMISSIONS } from '../context/RoleContext';
import { useUser } from '../context/UserContext';
import ProtectedRoute from '../components/ProtectedRoute';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiUsers, FiSearch, FiFilter, FiMoreVertical, FiEdit, FiTrash2,
  FiShield, FiCrown, FiUser, FiStar, FiPlus, FiX, FiCheck
} = FiIcons;

const UserManagement = () => {
  const { hasPermission } = useRole();
  const { allUsers, addUser, updateUser, deleteUser } = useUser();
  
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'user',
    isPremium: false,
    status: 'active'
  });

  useEffect(() => {
    setUsers(allUsers);
  }, [allUsers]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleRoleChange = (userId, newRole) => {
    const isPremium = newRole === 'premium' || newRole === 'admin' || newRole === 'moderator';
    updateUser(userId, { role: newRole, isPremium });
  };

  const handleStatusChange = (userId, newStatus) => {
    updateUser(userId, { status: newStatus });
  };

  const handleBulkAction = (action) => {
    if (selectedUsers.length === 0) return;

    switch (action) {
      case 'delete':
        selectedUsers.forEach(userId => deleteUser(userId));
        break;
      case 'premium':
        selectedUsers.forEach(userId => 
          updateUser(userId, { isPremium: true, role: 'premium' })
        );
        break;
      case 'suspend':
        selectedUsers.forEach(userId => 
          updateUser(userId, { status: 'suspended' })
        );
        break;
    }
    setSelectedUsers([]);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      isPremium: user.isPremium,
      status: user.status
    });
    setShowUserModal(true);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      role: 'user',
      isPremium: false,
      status: 'active'
    });
    setShowUserModal(true);
  };

  const handleSubmitUser = (e) => {
    e.preventDefault();
    
    if (editingUser) {
      // Update existing user
      updateUser(editingUser.id, userForm);
    } else {
      // Add new user
      addUser(userForm);
    }
    
    setShowUserModal(false);
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      role: 'user',
      isPremium: false,
      status: 'active'
    });
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser(userId);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return FiShield;
      case 'moderator': return FiCrown;
      case 'premium': return FiStar;
      default: return FiUser;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-red-400';
      case 'moderator': return 'text-purple-400';
      case 'premium': return 'text-yellow-400';
      default: return 'text-mystical-300';
    }
  };

  return (
    <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_USERS}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-2xl font-mystical font-bold text-white mb-2">
              User Management
            </h1>
            <p className="text-mystical-200">
              Manage users, roles, and permissions
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-mystical-300">{users.length}</div>
              <div className="text-sm text-mystical-200">Total Users</div>
            </div>
            <button
              onClick={handleAddUser}
              className="mystical-button px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add User</span>
            </button>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mystical-card rounded-xl p-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mystical-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:border-mystical-300"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-mystical-300"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admins</option>
              <option value="moderator">Moderators</option>
              <option value="premium">Premium</option>
              <option value="user">Users</option>
            </select>
          </div>
        </motion.div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mystical-card rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-white">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkAction('premium')}
                  className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded text-sm hover:bg-yellow-500/30"
                >
                  Make Premium
                </button>
                <button
                  onClick={() => handleBulkAction('suspend')}
                  className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded text-sm hover:bg-orange-500/30"
                >
                  Suspend
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 bg-red-500/20 text-red-300 rounded text-sm hover:bg-red-500/30"
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mystical-card rounded-xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-mystical-600/30">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(filteredUsers.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-mystical-200 font-semibold">User</th>
                  <th className="px-4 py-3 text-left text-mystical-200 font-semibold">Role</th>
                  <th className="px-4 py-3 text-left text-mystical-200 font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-mystical-200 font-semibold">Join Date</th>
                  <th className="px-4 py-3 text-left text-mystical-200 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className="border-b border-mystical-600/30 hover:bg-mystical-600/20"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(prev => [...prev, user.id]);
                          } else {
                            setSelectedUsers(prev => prev.filter(id => id !== user.id));
                          }
                        }}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-white font-medium">{user.name}</div>
                        <div className="text-mystical-300 text-sm">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <SafeIcon 
                          icon={getRoleIcon(user.role)} 
                          className={`w-4 h-4 ${getRoleColor(user.role)}`} 
                        />
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(user.role)} bg-current bg-opacity-20`}>
                          {user.role}
                        </span>
                        {user.isPremium && (
                          <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.status === 'active' 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-mystical-200 text-sm">
                      {new Date(user.joinDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1 text-mystical-300 hover:text-white rounded"
                          title="Edit User"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-1 text-mystical-300 hover:text-red-300 rounded"
                          title="Delete User"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="mystical-card rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">
              {users.filter(u => u.status === 'active').length}
            </div>
            <div className="text-mystical-200 text-sm">Active Users</div>
          </div>
          <div className="mystical-card rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {users.filter(u => u.isPremium).length}
            </div>
            <div className="text-mystical-200 text-sm">Premium Users</div>
          </div>
          <div className="mystical-card rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">
              {users.filter(u => u.role === 'moderator').length}
            </div>
            <div className="text-mystical-200 text-sm">Moderators</div>
          </div>
          <div className="mystical-card rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-red-400">
              {users.filter(u => u.role === 'admin').length}
            </div>
            <div className="text-mystical-200 text-sm">Admins</div>
          </div>
        </motion.div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mystical-card rounded-xl p-6 w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-mystical font-bold text-white">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-mystical-300 hover:text-white"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitUser} className="space-y-4">
              <div>
                <label className="block text-mystical-200 text-sm font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:border-mystical-300"
                  required
                />
              </div>

              <div>
                <label className="block text-mystical-200 text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:border-mystical-300"
                  required
                />
              </div>

              <div>
                <label className="block text-mystical-200 text-sm font-medium mb-2">
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    const isPremium = role === 'premium' || role === 'admin' || role === 'moderator';
                    setUserForm({...userForm, role, isPremium});
                  }}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-mystical-300"
                >
                  <option value="user">User</option>
                  <option value="premium">Premium</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-mystical-200 text-sm font-medium mb-2">
                  Status
                </label>
                <select
                  value={userForm.status}
                  onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-mystical-300"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPremium"
                  checked={userForm.isPremium}
                  onChange={(e) => setUserForm({...userForm, isPremium: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="isPremium" className="text-mystical-200">
                  Premium Access
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 mystical-button py-3 rounded-lg font-semibold"
                >
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </ProtectedRoute>
  );
};

export default UserManagement;